// Pure types, defaults, and sanitizers for CMS configuration.
// No server-only imports (e.g. prisma) so this module is safe to import
// from client components.

// ─── Types ────────────────────────────────────────────────────────
export type CtaVariant = "primary" | "outline" | "ghost";
export type CtaIcon = "none" | "cart" | "send" | "arrow" | "user" | "play";
// Zachowanie/wygląd paska nawigacji:
// - sticky:   klasyczny pasek na całą szerokość, zawsze przyklejony u góry
// - hide:     pełna szerokość; chowa się przy przewijaniu w dół, wraca w górę
// - floating: zawsze odklejona, zaokrąglona „wyspa” z odstępem od góry
// - island:   styl Apple — pełny pasek u góry, zwija się w wyśrodkowaną wyspę
//             i chowa podczas przewijania (rekomendowane)
export type HeaderMode = "sticky" | "hide" | "floating" | "island";
export const HEADER_MODES: HeaderMode[] = ["island", "floating", "hide", "sticky"];
export type SocialPlatform =
  | "facebook"
  | "instagram"
  | "youtube"
  | "twitter"
  | "linkedin"
  | "tiktok";

export interface NavLink {
  id: string;
  label: string;
  href: string;
  openInNewTab?: boolean;
}

export interface CtaButton {
  id: string;
  label: string;
  href: string;
  variant: CtaVariant;
  icon?: CtaIcon;
  openInNewTab?: boolean;
}

export interface HeaderConfig {
  navLinks: NavLink[];
  ctaButtons: CtaButton[];
  showCart: boolean;
  headerMode: HeaderMode;
}

export interface FooterColumn {
  id: string;
  title: string;
  links: NavLink[];
}

export interface SocialLink {
  id: string;
  platform: SocialPlatform;
  href: string;
}

export interface PaymentLogo {
  id: string;
  name: string;
  imageUrl: string;
  enabled: boolean;
}

export interface FooterConfig {
  description: string;
  socialLinks: SocialLink[];
  columns: FooterColumn[];
  bottomLinks: NavLink[];
  paymentLogos: PaymentLogo[];
  copyright: string; // może zawierać {year}
}

// ─── Defaults ─────────────────────────────────────────────────────
export const DEFAULT_HEADER: HeaderConfig = {
  navLinks: [
    { id: "h1", label: "Nasze produkty", href: "#produkty" },
    { id: "h2", label: "Turbo Weekend", href: "#turbo" },
    { id: "h3", label: "Kursy", href: "#kursy" },
    { id: "h4", label: "Opinie", href: "#opinie" },
    { id: "h5", label: "LIVE", href: "#live" },
  ],
  ctaButtons: [
    { id: "cta1", label: "Platforma", href: "/platforma/login", variant: "primary", icon: "none" },
  ],
  showCart: true,
  headerMode: "island",
};

// Zakres konfiguracji nagłówka: globalny albo dedykowany dla landingu.
export type CmsHeaderScope = "global" | "ambassador" | "recruitment" | "kursyMaturalne" | "matura2027";

// Domyślny nagłówek landingu rekrutacji (linki-kotwice + „Aplikuj”).
export const DEFAULT_HEADER_RECRUITMENT: HeaderConfig = {
  navLinks: [
    { id: "r1", label: "Dlaczego my", href: "#wyroznia" },
    { id: "r2", label: "Co oferujemy", href: "#funkcje" },
    { id: "r3", label: "Jak to działa", href: "#jak-to-dziala" },
    { id: "r4", label: "FAQ", href: "#faq" },
  ],
  ctaButtons: [
    { id: "rcta", label: "Aplikuj", href: "#formularz", variant: "primary", icon: "send" },
  ],
  showCart: false,
  headerMode: "island",
};

// Domyślny nagłówek landingu ambasadorskiego (dziedziczy globalny — pusty
// oznacza „użyj globalnego”; zapewniamy sensowny start przy włączeniu własnego).
export const DEFAULT_HEADER_AMBASSADOR: HeaderConfig = {
  navLinks: [
    { id: "a1", label: "Jak to działa", href: "#jak-to-dziala" },
    { id: "a2", label: "Zarobki", href: "#zarobki" },
    { id: "a3", label: "FAQ", href: "#faq" },
  ],
  ctaButtons: [
    { id: "acta", label: "Zostań ambasadorem", href: "#formularz", variant: "primary", icon: "arrow" },
  ],
  showCart: false,
  headerMode: "island",
};

export const DEFAULT_FOOTER: FooterConfig = {
  description: "Przygotuj się do matury 2027 z NextSchool.",
  socialLinks: [],
  columns: [
    {
      id: "c1",
      title: "Platforma",
      links: [
        { id: "c1l2", label: "Jak to działa", href: "/kursy-maturalne" },
      ],
    },
    {
      id: "c2",
      title: "Kursy",
      links: [
        { id: "c2l1", label: "Matematyka", href: "/kursy-maturalne" },
        { id: "c2l2", label: "Język Polski", href: "/kursy-maturalne" },
      ],
    },
    {
      id: "c3",
      title: "Pomoc",
      links: [
        { id: "c3l2", label: "Kontakt", href: "/kontakt" },
        { id: "c3l3", label: "Regulamin", href: "/dokumenty/regulamin" },
        { id: "c3l4", label: "Polityka prywatności", href: "/dokumenty/polityka-prywatnosci" },
      ],
    },
    {
      id: "c4",
      title: "Kontakt",
      links: [
        { id: "c4l1", label: "kontakt@nextschool.pl", href: "mailto:kontakt@nextschool.pl" },
      ],
    },
  ],
  bottomLinks: [
    { id: "b1", label: "Regulamin", href: "/dokumenty/regulamin" },
    { id: "b2", label: "Prywatność", href: "/dokumenty/polityka-prywatnosci" },
    { id: "b3", label: "Cookies", href: "/polityka-cookies" },
  ],
  paymentLogos: [
    {
      id: "pay-blik",
      name: "BLIK",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/7/7c/Blik_logo.jpg",
      enabled: true,
    },
    {
      id: "pay-p24",
      name: "Przelewy24",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/e/e3/Przelewy24_logo.png",
      enabled: true,
    },
  ],
  copyright: "© {year} NextSchool. Wszystkie prawa zastrzeżone.",
};

// ─── Sanitization ─────────────────────────────────────────────────
function s(v: unknown, max = 300): string {
  if (typeof v !== "string") return "";
  return v.trim().slice(0, max);
}

function sanitizeNavLink(v: unknown, idx: number): NavLink | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const label = s(o.label, 80);
  const href = s(o.href, 500);
  if (!label || !href) return null;
  return {
    id: s(o.id, 40) || `n${idx}_${Date.now().toString(36)}`,
    label,
    href,
    openInNewTab: !!o.openInNewTab,
  };
}

function sanitizePaymentLogo(v: unknown, idx: number): PaymentLogo | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const name = s(o.name, 40);
  const imageUrl = s(o.imageUrl, 500);
  if (!name || !imageUrl) return null;
  // Tylko bezpieczne źródła obrazka (http(s) lub ścieżka root-relative).
  if (!/^(https?:\/\/|\/)/i.test(imageUrl)) return null;
  return {
    id: s(o.id, 40) || `pay${idx}_${Date.now().toString(36)}`,
    name,
    imageUrl,
    enabled: o.enabled === undefined ? true : !!o.enabled,
  };
}

function sanitizeCta(v: unknown, idx: number): CtaButton | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const label = s(o.label, 60);
  const href = s(o.href, 500);
  if (!label || !href) return null;
  const variant: CtaVariant =
    o.variant === "outline" || o.variant === "ghost" ? (o.variant as CtaVariant) : "primary";
  const allowedIcons: CtaIcon[] = ["none", "cart", "send", "arrow", "user", "play"];
  const icon: CtaIcon = allowedIcons.includes(o.icon as CtaIcon) ? (o.icon as CtaIcon) : "none";
  return {
    id: s(o.id, 40) || `c${idx}_${Date.now().toString(36)}`,
    label,
    href,
    variant,
    icon,
    openInNewTab: !!o.openInNewTab,
  };
}

export function sanitizeHeader(input: unknown): HeaderConfig {
  const o = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const navLinks = Array.isArray(o.navLinks)
    ? (o.navLinks.map((l, i) => sanitizeNavLink(l, i)).filter(Boolean) as NavLink[])
    : DEFAULT_HEADER.navLinks;
  const ctaButtons = Array.isArray(o.ctaButtons)
    ? (o.ctaButtons.map((c, i) => sanitizeCta(c, i)).filter(Boolean) as CtaButton[])
    : DEFAULT_HEADER.ctaButtons;
  const headerMode: HeaderMode = HEADER_MODES.includes(o.headerMode as HeaderMode)
    ? (o.headerMode as HeaderMode)
    : DEFAULT_HEADER.headerMode;
  return {
    navLinks: navLinks.slice(0, 20),
    ctaButtons: ctaButtons.slice(0, 6),
    showCart: typeof o.showCart === "boolean" ? o.showCart : true,
    headerMode,
  };
}

export function sanitizeFooter(input: unknown): FooterConfig {
  const o = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const allowedPlatforms: SocialPlatform[] = [
    "facebook",
    "instagram",
    "youtube",
    "twitter",
    "linkedin",
    "tiktok",
  ];
  const socialLinks = Array.isArray(o.socialLinks)
    ? o.socialLinks
        .map((v, i) => {
          if (!v || typeof v !== "object") return null;
          const ob = v as Record<string, unknown>;
          const platform = (ob.platform as SocialPlatform) || "facebook";
          if (!allowedPlatforms.includes(platform)) return null;
          const href = s(ob.href, 500);
          if (!href) return null;
          return {
            id: s(ob.id, 40) || `s${i}_${Date.now().toString(36)}`,
            platform,
            href,
          } as SocialLink;
        })
        .filter(Boolean) as SocialLink[]
    : DEFAULT_FOOTER.socialLinks;

  const columns = Array.isArray(o.columns)
    ? o.columns
        .map((c, i) => {
          if (!c || typeof c !== "object") return null;
          const co = c as Record<string, unknown>;
          const title = s(co.title, 60);
          if (!title) return null;
          const links = Array.isArray(co.links)
            ? (co.links.map((l, j) => sanitizeNavLink(l, j)).filter(Boolean) as NavLink[])
            : [];
          return {
            id: s(co.id, 40) || `col${i}_${Date.now().toString(36)}`,
            title,
            links: links.slice(0, 20),
          } as FooterColumn;
        })
        .filter(Boolean) as FooterColumn[]
    : DEFAULT_FOOTER.columns;

  const bottomLinks = Array.isArray(o.bottomLinks)
    ? (o.bottomLinks.map((l, i) => sanitizeNavLink(l, i)).filter(Boolean) as NavLink[])
    : DEFAULT_FOOTER.bottomLinks;

  const paymentLogos = Array.isArray(o.paymentLogos)
    ? (o.paymentLogos.map((l, i) => sanitizePaymentLogo(l, i)).filter(Boolean) as PaymentLogo[])
    : DEFAULT_FOOTER.paymentLogos;

  return {
    description: s(o.description, 500) || DEFAULT_FOOTER.description,
    socialLinks: socialLinks.slice(0, 10),
    columns: columns.slice(0, 6),
    bottomLinks: bottomLinks.slice(0, 10),
    paymentLogos: paymentLogos.slice(0, 12),
    copyright: s(o.copyright, 200) || DEFAULT_FOOTER.copyright,
  };
}
